<template>
  <Dialog
    v-model="useDialogStore().templateDuplicate.isOpen"
    size="small"
    unbounded
    @open="onOpen"
    @closed="onClosed"
  >
    <template #header="{ close }">
      <DialogHeader
        :title="dialogTitle"
        :show-close="true"
        @close="close"
      />
    </template>

    <div
      v-loading="isLoading"
      class="min-h-[8rem]"
    >
      <!-- Step 1: source template list -->
      <div
        v-if="step === 1"
        class="p-6 space-y-3"
      >
        <TextInput
          v-if="sourceTemplates.length > 3"
          v-model="sourceSearch"
          name="sourceSearch"
          :placeholder="useT('bcd328')"
          icon-left="search-md"
        />
        <EventDuplicateTemplateItem
          v-for="template in filteredSourceTemplates"
          :key="template._id"
          :template="template"
          @click="onSelectSource(template)"
        />
        <EmptyState
          v-if="!isLoading && !sourceTemplates.length"
          icon="file-search-02"
          :title="useT('bcd326')"
        />
        <EmptyState
          v-else-if="!isLoading && !filteredSourceTemplates.length"
          icon="file-search-02"
          :title="useT('bcd327')"
        />
      </div>

      <!-- Step 2: target workspace + event -->
      <div
        v-else-if="step === 2"
        class="p-6 space-y-4"
      >
        <EventDuplicateTemplateItem
          v-if="form.sourceTemplate"
          :template="form.sourceTemplate"
          readonly
        />
        <InputBlock
          :label="useT('bcd6')"
          prop="targetWorkspaceDomain"
        >
          <Select
            v-model="form.targetWorkspaceDomain"
            :options="workspaceOptions"
            filterable
            :placeholder="useT('bcd329')"
            :disabled="isLoadingWorkspaces"
            @change="onWorkspaceChange"
          />
        </InputBlock>
        <InputBlock
          :label="useT('bcd338')"
          prop="targetEventId"
        >
          <Select
            v-model="form.targetEventId"
            :options="eventOptions"
            filterable
            :placeholder="isLoadingEvents ? useT('bcd331') : useT('bcd330')"
            :disabled="!form.targetWorkspaceDomain || isLoadingEvents"
          />
        </InputBlock>
      </div>

      <!-- Step 3: optional email response template replacement -->
      <div
        v-else-if="step === 3"
        class="p-6 space-y-4"
      >
        <div class="text-sm text-gray-700">
          {{ useT('bcd332') }}
        </div>
        <InputBlock
          :label="useT('bcd333')"
          prop="emailResponseTemplateId"
        >
          <Select
            v-model="form.emailResponseTemplateId"
            :options="emailResponseOptions"
            filterable
            clearable
          />
        </InputBlock>
      </div>
    </div>

    <template
      v-if="step > 1"
      #footer
    >
      <ButtonGroup
        type="primary"
        :text-submit="step === lastStep ? useT('bcd320') : useT('bcd208')"
        :text-cancel="useT('bcd334')"
        align="right"
        :loading="isSubmitting"
        :disabled="!canAdvance"
        @submit="onAdvance"
        @cancel="onBack"
      />
    </template>
  </Dialog>
</template>

<script setup>
const hasEmailResponseRef = (template) => (
  template?.type === 'page'
  && Array.isArray(template?.sections)
  && template.sections.some((s) => s?.type === 'formBasic' && s?.data?.emailResponseTemplate)
);

const emptyForm = () => ({
  sourceTemplate: null,
  targetWorkspaceDomain: '',
  targetEventId: '',
  emailResponseTemplateId: '',
});

// Refs
const step = ref(1);
const sourceTemplates = ref([]);
const sourceSearch = ref('');
const targetEvents = ref([]);
const emailResponseTemplates = ref([]);
const form = ref(emptyForm());
const isLoading = ref(false);
const isLoadingEvents = ref(false);
const isSubmitting = ref(false);

// Computed
const dialogTitle = computed(() => {
  if (step.value === 1) return useT('bcd323');
  if (step.value === 2) return useT('bcd324');
  return useT('bcd325');
});

const filteredSourceTemplates = computed(() => {
  const query = sourceSearch.value.trim().toLowerCase();
  if (!query) return sourceTemplates.value;
  return sourceTemplates.value.filter((t) => (t.name || '').toLowerCase().includes(query));
});

const needsEmailResponseStep = computed(() => hasEmailResponseRef(form.value.sourceTemplate));

const lastStep = computed(() => (needsEmailResponseStep.value ? 3 : 2));

const workspaceOptions = computed(() => (
  (useWorkspacesStore().templateCopyWorkspaces || []).map((workspace) => {
    // `domain - Name` so the Select's built-in text filter matches both fields
    // and the domain (the stable identifier) leads the label.
    const text = workspace.name && workspace.name !== workspace.domain
      ? `${workspace.domain} - ${workspace.name}`
      : workspace.domain;
    return {
      id: workspace.domain,
      text,
    };
  })
));

const eventOptions = computed(() => (
  [...targetEvents.value]
    // Newest start date first so the most relevant events are at the top; oldest fall to the bottom.
    .sort((a, b) => new Date(b.general?.startDate || 0) - new Date(a.general?.startDate || 0))
    .map((event) => ({
      id: event._id,
      text: event.general?.title || useT('bcd336'),
      description: event.general?.startDate
        ? getFormattedDate(event.general.startDate, {
          lang: useAuthStore().user?.locale,
          format: 'll',
        })
        : undefined,
      keywords: event._id,
    }))
));

const emailResponseOptions = computed(() => (
  emailResponseTemplates.value.map((template) => ({
    id: template._id,
    text: template.name || useT('bcd337'),
  }))
));

const canAdvance = computed(() => {
  if (step.value === 2) {
    return Boolean(form.value.targetWorkspaceDomain && form.value.targetEventId);
  }
  if (step.value === 3) {
    return true; // empty is allowed (warning shown)
  }
  return false;
});

// Methods
const loadSourceTemplates = async() => {
  isLoading.value = true;

  sourceTemplates.value = await useTemplatesStore().fetchByEvent({
    workspaceDomain: useDialogStore().templateDuplicate.workspaceDomain,
    eventId: useDialogStore().templateDuplicate.eventId,
  });

  isLoading.value = false;
};

const isLoadingWorkspaces = ref(false);

const loadWorkspacesIfNeeded = async() => {
  if (useWorkspacesStore().templateCopyWorkspaces.length) return;
  isLoadingWorkspaces.value = true;
  await useWorkspacesStore().fetchWorkspacesForTemplateCopy();
  isLoadingWorkspaces.value = false;
};

const onWorkspaceChange = async() => {
  form.value.targetEventId = '';
  targetEvents.value = [];

  if (!form.value.targetWorkspaceDomain) return;

  isLoadingEvents.value = true;
  targetEvents.value = await useEventsStore().fetchEventsByWorkspace(form.value.targetWorkspaceDomain, { includeIgnored: true });
  isLoadingEvents.value = false;
};

const loadEmailResponseOptions = async() => {
  isLoading.value = true;
  emailResponseTemplates.value = await useTemplatesStore().fetchByEvent({
    workspaceDomain: form.value.targetWorkspaceDomain,
    eventId: form.value.targetEventId,
    type: 'email',
  });
  isLoading.value = false;
};

const onSelectSource = (template) => {
  form.value.sourceTemplate = template;
  // Advance immediately so the click feels instant; fetches run in the background
  // and the step 2 fields show their own loading state.
  step.value = 2;
  // Default target to the source workspace to match the common "same workspace, different event" case.
  form.value.targetWorkspaceDomain = useDialogStore().templateDuplicate.workspaceDomain || '';
  loadWorkspacesIfNeeded();
  if (form.value.targetWorkspaceDomain) {
    onWorkspaceChange();
  }
};

const onBack = () => {
  if (step.value === 2) {
    step.value = 1;
    form.value.targetWorkspaceDomain = '';
    form.value.targetEventId = '';
    targetEvents.value = [];
    return;
  }
  if (step.value === 3) {
    step.value = 2;
    form.value.emailResponseTemplateId = '';
    emailResponseTemplates.value = [];
  }
};

const submitDuplicate = async() => {
  isSubmitting.value = true;

  const response = await useTemplatesStore().duplicate({
    templateId: form.value.sourceTemplate._id,
    sourceWorkspaceDomain: useDialogStore().templateDuplicate.workspaceDomain,
    targetWorkspaceDomain: form.value.targetWorkspaceDomain,
    targetEventId: form.value.targetEventId,
    emailResponseTemplateId: form.value.emailResponseTemplateId || null,
  });

  isSubmitting.value = false;

  if (response) {
    useToast(useT('bcd335'));
    useDialogStore().templateDuplicate.onSubmit?.(response);
    useDialogStore().close('templateDuplicate');
  }
};

const onAdvance = async() => {
  if (step.value === 2) {
    if (needsEmailResponseStep.value) {
      await loadEmailResponseOptions();
      step.value = 3;
      return;
    }
    await submitDuplicate();
    return;
  }

  if (step.value === 3) {
    await submitDuplicate();
  }
};

const resetState = () => {
  step.value = 1;
  form.value = emptyForm();
  sourceTemplates.value = [];
  sourceSearch.value = '';
  targetEvents.value = [];
  emailResponseTemplates.value = [];
};

const onOpen = async() => {
  resetState();
  await loadSourceTemplates();
};

const onClosed = () => {
  resetState();
};
</script>
